// ============= Imports =============
// External dependencies
import { useCallback } from 'react';

// Internal dependencies - APIs
import { fetchGraphUsers } from '../apis/graph';

// Internal dependencies - Types
import { AuthContextProps } from '@/contexts/AuthContext';
import { GraphDataHook } from '../components/container/forms/types';

// ============= Types =============
/**
 * Type for the Graph API response data
 * @template T - Type of the value property in the response
 */

// ============= Hook Implementation =============
/**
 * Custom hook for handling Graph API data fetching operations
 * Provides a wrapped function to fetch user data from Microsoft Graph API
 *
 * @param getAccessToken - Function to retrieve authentication token
 * @returns Object containing the fetchGraphData function
 */
export const useGraphData = (
  getAccessToken: AuthContextProps['getAccessToken']
): GraphDataHook => {
  /**
   * Fetches user data from Graph API based on search term
   *
   * @param searchTerm - Term to search for in Graph API
   * @returns Promise resolving to user data or null if unsuccessful
   *
   * @example
   * ```typescript
   * const { fetchGraphData } = useGraphData(getAccessToken);
   * const users = await fetchGraphData('john.doe');
   * ```
   */
  const fetchGraphData = useCallback(
    async (searchTerm: string) => {
      // Return early if no search term provided
      if (!searchTerm) {
        return null;
      }

      try {
        // Fetch user data from Graph API
        const graphResult = await fetchGraphUsers({
          getAccessToken,
          searchValue: searchTerm,
        });

        // Return data if successful
        if (graphResult.success && graphResult.data) {
          return graphResult.data.value;
        }

        return null;
      } catch (error: unknown) {
        console.error('Error fetching protected data:', error);
        return null;
      }
    },
    [getAccessToken]
  );

  return { fetchGraphData };
};

// ============= Usage Example =============
/**
 * Example usage of useGraphData hook:
 *
 * ```typescript
 * const { fetchGraphData } = useGraphData(getAccessToken);
 *
 * // In an async function:
 * const searchUsers = async (term: string) => {
 *   const users = await fetchGraphData(term);
 *   if (users) {
 *     // Handle users data
 *   }
 * };
 * ```
 */
